import {makeAutoObservable, runInAction, reaction} from 'mobx';
import {
    AdminOrganizationDTO,
    DeviceResponseDTO,
    FilterDTO,
    FilterGroupDTO,
    SiteInfoDTO
} from '../Models/ApiModels';
import {LocalStorageGet, LocalStorageSet} from '../Services/Helpers';
import {PromiseCompletion} from '../Services/PromiseCompletion';
import {getSelectOptions, Option} from '../Components';
import {ApiService} from '../Services';
import {ApiURL} from '../AppConstants';
import {authService} from "../index";

export type SelectedFilter = {
    customer: string
    site: string
    plant: string
}

const FILTER_STORE_KEY = 'filterState';

export class FiltersStore {
    public filters: FilterDTO = new FilterDTO();
    public selected: SelectedFilter = this.defaultFilter;
    public isIoTSubscription: boolean = false;
    public adminOrganizationSettings: AdminOrganizationDTO[] = [];
    public isGlobalSupport = authService.isGlobalSupport;
    public isAdmin = authService.isAdmin;

    isLoading: PromiseCompletion = new PromiseCompletion();

    constructor() {
        makeAutoObservable(this);
        reaction(
            () => this.selected.customer,
            (customer: string) => {
                if (customer && (this.isAdmin || this.isGlobalSupport)) {
                    void this._getFilterData(customer);
                }
                this.isIoTSubscription = this.adminOrganizationSettings?.find?.(org => org.id === customer)?.settings?.['subscriptions']?.includes('IoT')??false;
            }
        );
    }

    public async getData() {
        await this._getCustomers();
        if(!this.isAdmin && !this.isGlobalSupport){
            await this._getFilterData();
        }
        this.restoreState();
    }

    private async _getFilterData (organizationId?: string) {
        const params = organizationId ? {organizationId} : undefined;
        const siteReq = ApiService.getData<SiteInfoDTO[]>(ApiURL.Sites, params);
        const deviceReq = ApiService.getData<DeviceResponseDTO[]>(ApiURL.Devices, params);
        const getData = Promise.all([ siteReq, deviceReq]);
        this.isLoading.subscribe(getData);
        const [
            {data:site},
            {data:device}] = await getData;
        runInAction(() => {
            this.filters.sites = site.map(s => {
                return {
                    name: s.name,
                    id: s.id,
                };
            });
            this.filters.plants = device.map(d => {
                const selected = site.find(s => s.id === d.siteId);
                return {
                    name: d.name,
                    id: d.id,
                    siteId: selected!.id,
                };
            });
        });
    }

    private async _getCustomers () {
        const [{data: customers}, {data: adminOrganizationSettings}] = await Promise.all([ApiService.getData<FilterGroupDTO[]>(ApiURL.Organizations, null, { completion: this.isLoading }), ApiService.getData<AdminOrganizationDTO[]>(ApiURL.AdminOrganizationSettings, null, { completion: this.isLoading }, true)]);

        runInAction(() => {
            this.filters.customers = customers;
            this.adminOrganizationSettings = adminOrganizationSettings;
        });
    }

    public get customerOptions(): Option[] {
        return getSelectOptions<FilterGroupDTO>(this.filters?.customers || [], 'name', 'id');
    }

    public get siteOptions(): Option[] {
        const sites = this.filters.sites;
        return getSelectOptions<FilterGroupDTO>(sites, 'name', 'id');
    }

    public get plantOptions (): Option[] {
        const plants = this.filters?.plants ?? [];

        const filtered = plants.filter(e => {
            if (this.selected.site) {
                return e.siteId === this.selected.site;
            }

            return true;
        });

        const mapped = filtered.map(p => ({
            ...p,
            name: `${p.name} (${p.id})`
        }));

        return getSelectOptions<FilterGroupDTO>(mapped, 'name', 'id');
    }

    public get selectedCustomerOption() {
        return this.customerOptions.find(o => o.value === this.selected.customer);
    }

    public get selectedSiteOption() {
        return this.siteOptions.find(o => o.value === this.selected.site);
    }

    public get selectedPlantOption() {
        return this.plantOptions.find(o => o.value === this.selected.plant);
    }

    public onCustomerChange = (selected: string) => {
        const newFilters = this.defaultFilter;
        newFilters.customer = selected;
        this.onFilterChange(newFilters);
    };

    public onSiteChange = (selected: string) => {
        const newFilters = this.defaultFilter;
        const site = this.filters.sites.find(e => e.id === selected);
        newFilters.customer = this.selected.customer;
        if (selected && site) {
            newFilters.site = site.id;
        }
        this.onFilterChange(newFilters);
    };

    public onPlantChange = (selected: string) => {
        const newFilters = this.defaultFilter;
        const plant = this.filters.plants.find(e => e.id === selected);
        newFilters.customer = this.selected.customer;
        if (selected && plant) {
            newFilters.site = plant.siteId;
            newFilters.plant = plant.id;
        } else {
            newFilters.site = this.selected.site;
        }
        this.onFilterChange(newFilters);
    };

    public get defaultFilter() {
        return {
            customer: '',
            site: '',
            plant: ''
        };
    }

    public restoreState() {
        const hasValue = LocalStorageGet<SelectedFilter>(FILTER_STORE_KEY);

        if (hasValue && this.customerOptions.find(e => e.value === hasValue.customer)) {
            this.selected = hasValue;
        }
        if (!this.selected.customer && this.customerOptions.length) {
            this.onCustomerChange(this.customerOptions[0].value as string);
        }
    }

    public storeValue() {
        LocalStorageSet(FILTER_STORE_KEY, this.selected);
    }

    public onFilterChange(newFilters: SelectedFilter) {
        this.selected = newFilters;
        this.storeValue();
    }

}